import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client.js';
import {
  EVENT_TYPES, POKEMON_ROLES, formatRange, parseDate, typeColor,
} from '../lib/format.js';
import { VERDICT, VerdictBadge } from '../components/Verdict.jsx';
import { beobachteEigeneBilder } from '../lib/eigeneBilder.js';

/**
 * Wann ist es zu haben? Eine Auswahl, sich gegenseitig ausschliessend.
 *
 * Vorher lag hier alles in einer Reihe — Zeitpunkt, Shiny und "vorbei"
 * nebeneinander, obwohl das verschiedene Fragen sind. Wer "Shiny" wählte,
 * verlor damit ungewollt die Zeitauswahl.
 */
const WANN = [
  { key: 'live', label: 'Jetzt' },
  { key: 'upcoming', label: 'Bald' },
  { key: 'all', label: 'Alle' },
  { key: 'past', label: 'Vorbei' },
];

/**
 * Woher bekommt man es? Mehrfachauswahl, weil sich die Wege nicht
 * ausschliessen: Ein Pokémon kann aus Eiern UND aus Raids kommen.
 */
const WOHER = [
  { key: 'event', label: 'Event' },
  { key: 'raid', label: 'Raid' },
  { key: 'ei', label: 'Ei' },
  { key: 'forschung', label: 'Forschung' },
];

// Reihenfolge der Gruppen: das Wertvollste zuerst.
const GRUPPEN = ['strong', 'solid', 'collection', 'unrated'];

/**
 * Wie viele Kacheln auf einmal.
 *
 * Nicht alles sofort: Bei knapp 250 Arten baut der Browser ein Raster auf,
 * das niemand am Stück ansieht — und mit eigenen Bildern kämen ebenso viele
 * Dateien dazu. Nachgeladen wird, sobald das Ende in Sicht kommt.
 */
const PORTION = 36;

/** Kürzel für die Kachel — ausgeschrieben passt es nicht in eine Drittelbreite. */
const QUELLE_KURZ = { ei: 'Ei', raid: 'Raid', forschung: 'Forsch.' };

/** Volltext für das Draufhalten. */
function quelleTitel(woher) {
  return woher
    .map((w) => (QUELLE_KURZ[w.quelle] ?? w.quelle) + (w.detail ? ': ' + w.detail : ''))
    .join(' · ');
}

export default function PokemonPage() {
  // Eigene Bilder aus den Scans — nur in der App vorhanden.
  const [eigeneBilder, setEigeneBilder] = useState({});

  useEffect(() => beobachteEigeneBilder(setEigeneBilder), []);

  const [pokemon, setPokemon] = useState(null);
  const [error, setError] = useState(null);
  // Voreinstellung "Jetzt": Das ist die Frage, mit der man die Seite oeffnet.
  const [filter, setFilter] = useState('live');
  const [suche, setSuche] = useState('');
  const [gruppiert, setGruppiert] = useState(true);

  // Quellen als Mehrfachauswahl. Leer heisst "alle" — das ist der Fall, in
  // dem man nichts eingeschränkt hat, und braucht keinen eigenen Knopf.
  const [quellen, setQuellen] = useState([]);

  // Nur solche mit erhöhter Shiny-Chance. Getrennt von der Zeitauswahl,
  // weil es eine andere Frage ist.
  const [nurShiny, setNurShiny] = useState(false);

  useEffect(() => {
    let aktiv = true;
    api.pokemon.list()
      .then((d) => aktiv && setPokemon(d.pokemon))
      .catch((e) => aktiv && setError(e.message));
    return () => { aktiv = false; };
  }, []);

  const sichtbar = useMemo(() => {
    if (!pokemon) return [];
    const begriff = suche.trim().toLowerCase();

    return pokemon
      .filter((mon) => {
        // Die Suche geht allem vor: Wer einen Namen eintippt, will ihn
        // finden — auch wenn ihn die eingestellten Filter ausschließen.
        if (begriff) {
          return (mon.name + ' ' + (mon.form ?? '')).toLowerCase().includes(begriff);
        }

        // Wann?
        if (filter !== 'all' && mon.status !== filter) return false;

        // "Vorbei" nur, wenn ausdrücklich gewählt. Sonst füllt es die Liste
        // mit Einträgen, bei denen es gerade nichts zu tun gibt.
        if (mon.status === 'past' && filter !== 'past') return false;

        // Woher? Leere Auswahl heißt "alle".
        if (quellen.length > 0) {
          const eigene = new Set((mon.woher ?? []).map((w) => w.quelle));

          if (mon.appearances.length > 0) {
            eigene.add('event');
          }

          if (!quellen.some((q) => eigene.has(q))) return false;
        }

        if (nurShiny && !mon.shinyBoostAhead) return false;

        return true;
      })
      // Innerhalb einer Gruppe zählt die Dringlichkeit: was läuft zuerst,
      // dann das zeitlich Nächste. Dex nur als Gleichstand-Entscheid.
      .sort((a, b) => {
        const rang = { live: 0, upcoming: 1, past: 2 };
        if (rang[a.status] !== rang[b.status]) return rang[a.status] - rang[b.status];
        if (a.next && b.next) {
          const diff = parseDate(a.next.startsAt) - parseDate(b.next.startsAt);
          if (diff !== 0) return diff;
        }
        return a.dexNo - b.dexNo;
      });
  }, [pokemon, filter, suche, quellen, nurShiny]);

  /**
   * In der Reihenfolge, in der auch angezeigt wird.
   *
   * Beim Gruppieren muss die Reihenfolge VOR dem Abschneiden feststehen.
   * Sonst landen nachgeladene Einträge in früheren Gruppen und schieben
   * alles darunter nach unten — man scrollt, und der Inhalt wandert weg.
   *
   * So angeordnet hängt sich jede Portion hinten an.
   */
  const geordnet = useMemo(() => (
    gruppiert
      ? GRUPPEN.flatMap((key) => sichtbar.filter((m) => m.verdict === key))
      : sichtbar
  ), [sichtbar, gruppiert]);

  // Wie viele davon gerade gezeigt werden.
  const [anzahl, setAnzahl] = useState(PORTION);
  const ende = useRef(null);

  // Bei jeder Änderung der Auswahl von vorn: Sonst stünde man mitten in
  // einer Liste, die inzwischen etwas anderes zeigt.
  useEffect(() => { setAnzahl(PORTION); }, [filter, suche, quellen, nurShiny, gruppiert]);

  useEffect(() => {
    const marke = ende.current;

    if (!marke || anzahl >= geordnet.length) {
      return undefined;
    }

    // Nachladen, sobald das Ende in Sicht kommt — nicht erst, wenn es
    // erreicht ist. Sonst sieht man beim Scrollen eine Lücke.
    const beobachter = new IntersectionObserver(
      (eintraege) => {
        if (eintraege[0].isIntersecting) {
          setAnzahl((n) => n + PORTION);
        }
      },
      { rootMargin: '400px' }
    );

    beobachter.observe(marke);

    return () => beobachter.disconnect();
  }, [anzahl, geordnet.length]);

  const gezeigt = useMemo(() => geordnet.slice(0, anzahl), [geordnet, anzahl]);

  /**
   * Bilder auch über die Dex-Nummer auffindbar machen.
   *
   * Die Dateien heißen nach der Kennung der Form — "glurak", "glurak-mega-x".
   * Wer die Grundform gescannt hat, hatte deshalb bei der Mega-Form einen
   * Platzhalter, obwohl dasselbe Pokémon gemeint ist.
   *
   * Die formlose Fassung gewinnt: Sie zeigt das Pokémon, wie es die meiste
   * Zeit aussieht.
   */
  const bilderNachArt = useMemo(() => {
    const nach = {};

    for (const mon of pokemon ?? []) {
      const bild = eigeneBilder[mon.slug];

      if (bild && (!nach[mon.dexNo] || !mon.form)) {
        nach[mon.dexNo] = bild;
      }
    }

    return nach;
  }, [pokemon, eigeneBilder]);

  const bildFuer = (mon) => eigeneBilder[mon.slug] ?? bilderNachArt[mon.dexNo];

  const starke = (pokemon ?? []).filter((m) => m.verdict === 'strong').length;

  return (
    <>
      <section className="hero">
        <span className="hero__tag">
          {starke > 0 ? `${starke} davon wirklich stark im Raid` : 'Pokémon aus Events'}
        </span>
        <h1>Lohnt sich, oder nur für den Dex?</h1>
        <p className="hero__sub">
          Alle Pokémon aus den erfassten Events — getrennt danach, ob sie im Raid
          etwas taugen oder nur die Sammlung füllen.
        </p>
      </section>

      <div className="filterleiste">
        <input
          className="input filterleiste__suche"
          type="search"
          value={suche}
          onChange={(e) => setSuche(e.target.value)}
          placeholder="Name suchen…"
          aria-label="Pokémon suchen"
        />

        {/* Während einer Suche sind die Filter wirkungslos — sie
            auszublenden ist ehrlicher, als sie bedienbar zu lassen. */}
        {suche.trim() === '' && (
          <>
            <div className="filterleiste__reihe">
              <span className="filterleiste__titel">Wann</span>
              {WANN.map((f) => (
                <button
                  key={f.key}
                  type="button"
                  className={filter === f.key ? 'chip chip--on' : 'chip'}
                  onClick={() => setFilter(f.key)}
                >
                  {f.label}
                </button>
              ))}
            </div>

            <div className="filterleiste__reihe">
              <span className="filterleiste__titel">Woher</span>
              {WOHER.map((f) => (
                <button
                  key={f.key}
                  type="button"
                  className={quellen.includes(f.key) ? 'chip chip--on' : 'chip'}
                  onClick={() => setQuellen((alt) => (
                    alt.includes(f.key) ? alt.filter((q) => q !== f.key) : [...alt, f.key]
                  ))}
                >
                  {f.label}
                </button>
              ))}

              {quellen.length > 0 && (
                <button type="button" className="chip chip--leise" onClick={() => setQuellen([])}>
                  zurücksetzen
                </button>
              )}
            </div>

            <div className="filterleiste__reihe">
              <button
                type="button"
                className={nurShiny ? 'chip chip--on' : 'chip'}
                onClick={() => setNurShiny((v) => !v)}
              >
                ✦ Shiny-Chance erhöht
              </button>

              <button type="button" className="chip" onClick={() => setGruppiert((g) => !g)}>
                {gruppiert ? 'Nach Zeit sortieren' : 'Nach Raid-Wert gruppieren'}
              </button>
            </div>
          </>
        )}
      </div>

      {error && <div className="error">{error}</div>}

      {!pokemon && !error && (
        <div className="grid">
          {[0, 1, 2, 3, 4, 5].map((i) => <div key={i} className="skeleton" style={{ height: 150 }} />)}
        </div>
      )}

      {pokemon && sichtbar.length === 0 && (
        <div className="empty">
          <div className="empty__icon">✧</div>
          <p><strong>Nichts gefunden.</strong></p>
          <p>Andere Filter probieren — oder es sind noch keine passenden Events erfasst.</p>
        </div>
      )}

      {sichtbar.length > 0 && (gruppiert
        ? GRUPPEN.map((key) => {
            const mons = gezeigt.filter((m) => m.verdict === key);
            if (mons.length === 0) return null;
            const v = VERDICT[key];

            return (
              <section className={`gruppe gruppe--${key}`} key={key} style={{ '--gruppe-farbe': v.farbe }}>
                <div className="gruppe__kopf">
                  <h2 className="gruppe__titel">{v.label}</h2>
                  {/* Die Zahl zählt die ganze Gruppe, nicht nur das schon
                      Geladene — sonst stiege sie beim Scrollen an und man
                      wüsste nie, woran man ist. */}
                  <span className="gruppe__anzahl">
                    {sichtbar.filter((m) => m.verdict === key).length}
                  </span>
                </div>
                <p className="gruppe__text">{v.erklaerung}</p>
                <div className="mon-gitter">
                  {mons.map((mon) => <MonKarte key={mon.slug} mon={mon} bild={bildFuer(mon)} />)}
                </div>
              </section>
            );
          })
        : (
          <div className="mon-gitter">
            {gezeigt.map((mon) => (
              <MonKarte key={mon.slug} mon={mon} bild={bildFuer(mon)} />
            ))}
          </div>
        )
      )}

      {/* Die Marke am Ende löst das Nachladen aus. Sie steht ausserhalb der
          Gruppen, damit sie in beiden Ansichten wirkt. */}
      {anzahl < geordnet.length && (
        <div className="nachladen" ref={ende}>
          {gezeigt.length} von {geordnet.length}
        </div>
      )}
    </>
  );
}

/**
 * Kompakte Kachel.
 *
 * Vorher trug jede Karte den vollen Eventblock — Titel, Zeitraum, zwei
 * Marken. Bei 300 Punkten Mindestbreite passte auf dem Handy genau eine
 * nebeneinander, und die Liste wurde endlos.
 *
 * Jetzt steht nur, was beim Überfliegen zählt: Name, Typen, Einstufung und
 * wann es zu haben ist. Alles Weitere steht eine Berührung entfernt auf der
 * Detailseite — dort ist ohnehin mehr Platz dafür.
 */
function MonKarte({ mon, bild }) {
  const urteil = VERDICT[mon.verdict] ?? VERDICT.unrated;

  return (
    <Link
      to={'/pokemon/' + mon.slug}
      className="mon-kachel"
      style={{ '--card-accent': urteil.farbe }}
    >
      <div className="mon-kachel__kopf">
        <span className="mon-kachel__dex">#{String(mon.dexNo).padStart(3, '0')}</span>
        <VerfuegbarkeitBadge mon={mon} />
      </div>

      {/* Der Bildplatz ist IMMER da, auch ohne Bild.
          Sonst stehen Kacheln mit und ohne Bild unterschiedlich hoch und das
          Raster franst aus. Ohne eigenes Bild steht dort ein Platzhalter in
          den Typfarben — er sagt zugleich, dass hier noch eines fehlt. */}
      {bild ? (
        <img className="mon-kachel__bild" src={bild} alt="" loading="lazy" />
      ) : (
        <div
          className="mon-kachel__bild mon-kachel__bild--leer"
          style={{
            '--typ-eins': typeColor(mon.types[0]),
            '--typ-zwei': typeColor(mon.types[1] ?? mon.types[0]),
          }}
        >
          <span>{String(mon.dexNo).padStart(3, '0')}</span>
        </div>
      )}

      <div className="mon-kachel__name">
        {mon.name}
        {mon.form && <span className="mon-kachel__form">{mon.form}</span>}
      </div>

      <div className="mon-kachel__typen">
        {mon.types.map((t) => (
          <span className="type type--klein" key={t} style={{ '--type-color': typeColor(t) }}>
            {t}
          </span>
        ))}
      </div>

      <div className="mon-kachel__fuss">
        {/* Woher es kommt, wenn kein Event dahintersteht — ein Kürzel, weil
            die Kachel schmal ist. */}
        {mon.appearances.length === 0 && mon.woher?.length > 0 && (
          <span className="mon-kachel__quelle" title={quelleTitel(mon.woher)}>
            {QUELLE_KURZ[mon.woher[0].quelle] ?? '?'}
          </span>
        )}
        {mon.bestTier && <span className={'tier tier--' + mon.bestTier}>{mon.bestTier}</span>}
        {mon.shinyBoostAhead && <span className="mon-kachel__shiny" title="Shiny erhöht">✦</span>}
        {mon.appearances.length > 1 && (
          <span className="mon-kachel__anzahl" title={mon.appearances.length + ' Events'}>
            {mon.appearances.length}×
          </span>
        )}
      </div>
    </Link>
  );
}

/**
 * Wie bald ist das Pokémon wieder zu haben?
 *
 * Kurz gehalten, weil drei Kacheln nebeneinander stehen: "In 44 Tagen"
 * sprengt eine Drittelbreite, "44 T" nicht. Die volle Angabe erscheint beim
 * Draufhalten und steht auf der Detailseite.
 */
function VerfuegbarkeitBadge({ mon }) {
  if (mon.status === 'live') return <span className="badge badge--live">Jetzt</span>;
  if (mon.status === 'past') return <span className="badge badge--soft">vorbei</span>;

  const tage = Math.ceil((parseDate(mon.next.startsAt) - new Date()) / 86400000);
  const text = tage <= 0 ? 'Heute' : tage === 1 ? 'Morgen' : tage + ' T';

  return <span className="badge badge--soft" title={'In ' + tage + ' Tagen'}>{text}</span>;
}
